/**
 * Course-level summary of library content that has moved on.
 *
 * Without this, library updates are only discoverable by opening every unit — which means in
 * practice they are never discovered, and the promise of a library ("fix it once") quietly fails.
 * The outline is where an author looks at the whole course, so it is where "eleven of the
 * fourteen things you borrowed have changed" belongs.
 *
 * Grouped BY LIBRARY rather than listed per block, because that matches the decision: a library
 * gets republished as a batch, and an author reviewing updates thinks "what changed in the
 * policies library", not "what changed in block 7 of unit 3". The per-block choice — keep local
 * edits or take theirs — stays on the block, where the local edits are visible.
 *
 * Renders nothing at all when nothing is behind. A permanently-present panel saying "0 updates"
 * is furniture, and it trains people not to look.
 */

import { useQuery } from '@tanstack/react-query';
import { Badge, Icon } from '../../shared/ui';
import { courseLinkSummary } from '../../shared/lib/library-link';
import './library-updates.css';

export function LibraryUpdates({ courseKey }: { courseKey: string }) {
  const summary = useQuery({
    queryKey: ['library-summary', courseKey],
    queryFn: () => courseLinkSummary(courseKey),
    // A silent failure is correct here: this is an advisory panel, and a course with no library
    // content at all is the common case. Breaking the outline over it would be wrong.
    retry: false,
    staleTime: 60_000,
  });

  const rows = (summary.data ?? []).filter((row) => row.ready_to_sync_count > 0);
  if (rows.length === 0) return null;

  const total = rows.reduce((sum, row) => sum + row.ready_to_sync_count, 0);

  return (
    <section className="libupd" aria-label="Library updates">
      <header className="libupd__head">
        <span className="libupd__icon">
          <Icon name="sync" />
        </span>
        <div>
          <strong>
            {total} {total === 1 ? 'component has' : 'components have'} a newer version in a library
          </strong>
          <p>
            Open the unit each one is in to take the update, skip it, or keep your own changes.
          </p>
        </div>
      </header>

      <ul className="libupd__list">
        {rows.map((row) => (
          <li key={row.upstream_context_key}>
            <span className="libupd__lib">
              <Icon name="library_books" />
              {row.upstream_context_title || row.upstream_context_key}
            </span>
            <span className="libupd__counts">
              <Badge tone="warning">{row.ready_to_sync_count} to update</Badge>
              <span className="libupd__of">of {row.total_count} used in this course</span>
              {row.last_published_at && (
                <span className="libupd__when">
                  published {new Date(row.last_published_at).toLocaleDateString()}
                </span>
              )}
            </span>
          </li>
        ))}
      </ul>
    </section>
  );
}
